import React, { createContext, useContext, ReactNode } from 'react';
import { useTenant, TenantData } from '../hooks/useTenant';
import { clearTenantCache, invalidateTenantCache, debugCache } from '../services/tenantCache';

// Interface pour le contexte
interface TenantContextType {
  tenant: TenantData | null;
  loading: boolean;
  error: string | null;
  format: (template: string) => string;
  refreshTenant: () => void;
  clearCache: () => void;
  // Aperçu en temps réel des modifications locales (non persistées)
  previewConfig: any | null;
  setPreviewValue: (path: string, value: any) => void;
  clearPreview: () => void;
}

// Création du contexte
const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Props pour le provider
interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Provider pour les données du tenant
 */
export function TenantProvider({ children }: TenantProviderProps) {
  const { tenant, loading, error } = useTenant();
  const [previewConfig, setPreviewConfig] = React.useState<any | null>(null);

  // Fonction pour formater les chaînes avec les valeurs du tenant
  const format = (template: string): string => {
    if (!tenant) return template;
    
    return template.replace(/{([^}]+)}/g, (match, key) => {
      const value = key.split('.').reduce((obj: any, prop: string) => {
        return obj && obj[prop] !== undefined ? obj[prop] : undefined;
      }, tenant);
      
      return value !== undefined ? String(value) : match;
    });
  };

  // Helper: set profond par chemin 'a.b.c'
  const deepSet = (obj: any, path: string, value: any) => {
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (current[key] === undefined || current[key] === null || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }
    current[parts[parts.length - 1]] = value;
  };

  // Définir une valeur d'aperçu (merge sur la config actuelle du tenant)
  const setPreviewValue = (path: string, value: any): void => {
    setPreviewConfig((prev: any) => {
      // Base = config existante du tenant ou précédente preview
      const base: any = prev
        ? { ...prev }
        : tenant?.config
          ? JSON.parse(JSON.stringify(tenant.config))
          : {};
      deepSet(base, path, value);
      return base;
    });
  };

  const clearPreview = (): void => {
    setPreviewConfig(null);
  };

  // Fonction pour rafraîchir les données tenant (invalide le cache)
  const refreshTenant = (): void => {
    if (tenant) {
      invalidateTenantCache(tenant.id);
      // Recharger la page pour récupérer les nouvelles données
      window.location.reload();
    }
  };

  // Fonction pour nettoyer complètement le cache
  const clearCache = (): void => {
    clearTenantCache();
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 Cache tenant nettoyé manuellement');
    }
  };

  // Debug en développement
  if (process.env.NODE_ENV === 'development') {
    // Exposer les fonctions de debug sur window pour les tests
    (window as any).tenantDebug = {
      debugCache,
      clearCache,
      refreshTenant
    };
  }

  return (
    <TenantContext.Provider value={{ tenant, loading, error, format, refreshTenant, clearCache, previewConfig, setPreviewValue, clearPreview }}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook pour utiliser le contexte du tenant
 */
export function useTenantContext() {
  const context = useContext(TenantContext);
  
  if (context === undefined) {
    throw new Error("useTenantContext doit être utilisé à l'intérieur d'un TenantProvider");
  }
  
  return context;
}
